import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { Dispatch, KeyboardEvent, SetStateAction, useRef, useState } from 'react';

import { ActionIcon } from '@/components/ActionIcon';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';

import { useRouter } from '@/i18n/routing';

export type ModalProps = {
    children?: React.ReactNode;
    domainConfiguration: DomainConfigurationDto;
    className?: string;
    title?: string | React.JSX.Element;
    titleCenter?: boolean;
    titleClassName?: string;
    subTitle?: string | React.JSX.Element;
    subTitleClassName?: string;
    modalIsOpen?: boolean;
    setModalIsOpen?: Dispatch<SetStateAction<boolean>>;
    closeable?: boolean;
    showCloseButton?: boolean;
    showBackground?: boolean;
    showBackgroundBlur?: boolean;
    enableOutsideActions?: boolean;
    onClose?: () => void;
    beforeClose?: () => boolean | void;
};

function Modal(props: ModalProps): React.JSX.Element {
    const { domainConfiguration, className, onClose, beforeClose } = props;

    const t = useTranslations();

    const router = useRouter();

    const closeButton = useRef(null);

    let modalIsOpen = props?.modalIsOpen;
    let setModalIsOpen = props?.setModalIsOpen;
    const [state, setState] = useState<boolean>(true);
    if (!modalIsOpen && !setModalIsOpen) {
        modalIsOpen = state;
        setModalIsOpen = setState;
    }

    const closeable = props.closeable !== false;

    function onCloseInternal() {
        if (beforeClose && beforeClose() === false) {
            return;
        }
        setModalIsOpen(false);
        if (onClose) {
            onClose();
        } else if (onClose === undefined) {
            router.back();
        }
    }

    if (modalIsOpen === false) {
        return null;
    }

    return (
        <Dialog
            initialFocus={closeButton}
            open={modalIsOpen}
            onClose={() => {
                if (closeable) {
                    onCloseInternal();
                }
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement> & { enterKey }) => {
                const key = e.which || e.keyCode;
                if (key === e.enterKey || key === 13) {
                    e.preventDefault();
                }
            }}
            className={classnames('fixed z-modal-view inset-0 w-screen h-full', {
                'pointer-events-none': props.enableOutsideActions
            })}>
            <DialogBackdrop
                className={classnames('fixed inset-0 flex items-center justify-center w-full h-full py-22 md:p-24', {
                    'backdrop-blur-sm': props.showBackgroundBlur !== false
                })}
                onTouchStart={(e) => {
                    e.stopPropagation();
                }}
                onTouchMove={(e) => {
                    e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                    e.stopPropagation();
                }}>
                <div
                    className={classnames('absolute inset-0 transition-all duration-300')}
                    style={{
                        backgroundColor:
                            props.showBackground !== false
                                ? domainConfiguration.style.backgroundColor || '#000000'
                                : '',
                        filter: 'brightness(25%)',
                        opacity: 0.3
                    }}
                />
            </DialogBackdrop>

            {/* If justify center is needed pass it down from where you need it */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel
                    className={classnames(
                        className,
                        'relative flex flex-col',
                        'w-full mx-16 max-h-mobile-modal',
                        'sm:h-auto sm:min-h-0 sm:max-h-full',
                        'modal-window',
                        'rounded-8 overflow-hidden',
                        'p-0',
                        {
                            'pointer-events-auto': props.enableOutsideActions
                        }
                    )}
                    style={{
                        background: withOpacity(domainConfiguration.style.backgroundColor, 1.0),
                        color: domainConfiguration.style.onSurfaceColor
                    }}>
                    <div
                        className="w-full h-full grow flex flex-col p-8 md:p-24"
                        style={{
                            backgroundColor: domainConfiguration.style.surfaceColor
                        }}>
                        {(props.title || props.subTitle) && (
                            <DialogTitle
                                className={classnames(
                                    'relative flex-none pb-18 md:pb-16 mt-24 md:mt-0',
                                    props.titleClassName || 'text-headline-24',
                                    {
                                        'text-center mx-auto': props.titleCenter
                                    }
                                )}>
                                {props.title}
                                {props.subTitle && (
                                    <div
                                        className={classnames(props.subTitleClassName || 'text-body-14 tracking-wide')}
                                        style={{
                                            color: domainConfiguration.style.onSurfaceColor,
                                            opacity: 0.5
                                        }}>
                                        {props.subTitle}
                                    </div>
                                )}
                            </DialogTitle>
                        )}

                        {closeable && props.showCloseButton !== false && (
                            <div className="absolute right-8 top-8 md:top-24 md:right-24 text-gray-middle">
                                <ActionIcon
                                    icon={
                                        <IconClose24
                                            fill={domainConfiguration.style.onSurfaceColor}
                                            className="opacity-60"
                                        />
                                    }
                                    action={onCloseInternal}
                                    // tooltipDirection={TooltipDirection.Left}
                                    tooltipText={t('common.close')}
                                />
                            </div>
                        )}

                        {props.children}
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default Modal;
